import React, { useCallback, useState } from "react";

import CustomButton from "components/common/Button";
import InlineLoader from "./InlineLoader";
import { hasPermission } from "hooks/usePermission";
import { listBillingSubscriptions } from "services/billing";
import type { BsPaginated } from "services/billing";

import CustomerFilter from "./CustomerFilter";
import SubscriptionStatusModal from "./SubscriptionStatusModal";
import { useCursorList } from "./useCursorList";
import { CustomChip, TableComponent } from "./ui";
import FilterSelect from "./FilterSelect";
import {
  BsSubscription,
  ON_BS_SINCE_LABEL,
  SUBSCRIPTION_STATUS_OPTIONS,
  formatDate,
  formatMoney,
  statusColor,
  titleCase,
} from "./types";

const baseColumns = [
  { id: "customerName", label: "Customer", render: (v: string) => v || "—" },
  { id: "planName", label: "Plan" },
  {
    id: "status",
    label: "Status",
    render: (v: string) => <CustomChip label={titleCase(v)} size="small" color={statusColor(v)} />,
  },
  { id: "amountCents", label: "Amount", render: (v: number, row: BsSubscription) => formatMoney(v, row.currency) },
  { id: "nextBillingDate", label: "Next billing", render: (v: string) => formatDate(v) },
  { id: "createdAt", label: ON_BS_SINCE_LABEL, render: (v: string) => formatDate(v) },
];

export default function SubscriptionsTab() {
  const canManage = hasPermission("billing.manage");
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState("");
  const load = useCallback(
    (cursor?: string) =>
      listBillingSubscriptions({ customerId, status: status || undefined, cursor, limit: 25 }) as Promise<
        BsPaginated<BsSubscription>
      >,
    [customerId, status]
  );
  const { rows, hasMore, loading, loadingMore, error, loadMore, reload } = useCursorList<BsSubscription>(load);
  const [selected, setSelected] = useState<BsSubscription | null>(null);

  // Only active↔paused are toggleable; pending/canceled have no action.
  const actionColumn = {
    id: "actions",
    label: "Actions",
    render: (_v: unknown, row: BsSubscription) => {
      if (row.status !== "active" && row.status !== "paused") return "—";
      return (
        <CustomButton
          label={row.status === "active" ? "Pause" : "Resume"}
          onClick={() => setSelected(row)}
        />
      );
    },
  };

  const columns = canManage ? [...baseColumns, actionColumn] : baseColumns;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-1 items-end" style={{ flexWrap: "wrap" }}>
        <CustomerFilter onChange={setCustomerId} />
        <FilterSelect
          name="subscriptions-status"
          label="Status"
          options={SUBSCRIPTION_STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
        />
      </div>
      {loading ? (
        <InlineLoader />
      ) : error ? (
        <p className="para-body-m-regular color-text-body-secondary">{error}</p>
      ) : (
        <>
          <TableComponent columns={columns} data={rows} disableSort />
          {hasMore && (
            <div className="flex justify-center mt-05">
              <CustomButton label={loadingMore ? "Loading…" : "Load more"} onClick={loadMore} disabled={loadingMore} />
            </div>
          )}
        </>
      )}
      {selected && (
        <SubscriptionStatusModal subscription={selected} onClose={() => setSelected(null)} onDone={reload} />
      )}
    </div>
  );
}
